import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { requestPasswordReset } from '@services/api';
import { SaaeLogo } from '@components/SaaeLogo';
import { Button } from '@components/Button/Button';
import { InputField } from '@components/Input';
import { useLanguage } from '@hooks/useLanguage';
import { forgotPasswordSchema, type ForgotPasswordValues } from '@schemas/formSchemas';

export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const isRtl = language === 'ar';

  const form = useForm<ForgotPasswordValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
  });

  async function handleSubmit(values: ForgotPasswordValues) {
    setLoading(true);
    try {
      await requestPasswordReset(values.email);
    } finally {
      setLoading(false);
      setSubmitted(true);
    }
  }

  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center bg-surface-web px-4 py-10 font-sans"
      dir={isRtl ? 'rtl' : 'ltr'}
    >
      <div className="mb-8 flex justify-center">
        <SaaeLogo size="small" mode="light" className="!h-9" />
      </div>
      <div className="w-full max-w-md rounded-card border border-surface-border bg-surface-cards p-6 shadow-sm sm:p-8">
        <h1 className="mb-1 text-center text-xl font-bold text-primary font-sans sm:text-2xl">
          {t('admin.passwordReset.requestTitle')}
        </h1>
        {submitted ? (
          <p className="mt-4 text-center text-sm text-primary/70 font-sans">
            {t('admin.passwordReset.sentNotice')}
          </p>
        ) : (
          <form onSubmit={form.handleSubmit(handleSubmit)} className="mt-6 flex flex-col gap-4" noValidate>
            <InputField
              label={t('admin.login.emailLabel')}
              type="email"
              autoComplete="email"
              size="lg"
              fullWidth
              contentDir="ltr"
              state={form.formState.errors.email ? 'error' : 'default'}
              {...form.register('email')}
            />
            {form.formState.errors.email && (
              <p className="text-xs text-surface-error-primary">{form.formState.errors.email.message}</p>
            )}
            <Button type="submit" variant="primary" size="lg" fullWidth disabled={loading} loading={loading}>
              {t('admin.passwordReset.requestSubmit')}
            </Button>
          </form>
        )}
        <div className="mt-6 text-center">
          <Link to="/login" className="text-sm text-brand-primary hover:underline">
            {t('admin.login.backToLogin')}
          </Link>
        </div>
      </div>
    </div>
  );
}
